import * as React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
  ListSubheader,
} from '@mui/material';
import { useQuery } from 'react-query';
import { apiClient } from '../../services/apiClient';
import { formatUnitOfMeasurement } from '../../utils/unitOfMeasurementFormatter';

export interface UnitOfMeasurementDropdownProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  onBlur?: () => void;
  label?: string;
  defaultUnitOfMeasurement?: string | null;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  size?: 'small' | 'medium';
  showDefaultLabel?: boolean; // Se true, mostra "(default)" accanto all'unità di default
}

export const UnitOfMeasurementDropdown: React.FC<UnitOfMeasurementDropdownProps> = ({
  value,
  onChange,
  onBlur,
  label = 'Unità di misura',
  defaultUnitOfMeasurement,
  error = false,
  helperText,
  disabled = false,
  fullWidth = true,
  required = false,
  variant = 'outlined',
  size = 'medium',
  showDefaultLabel = true,
}) => {
  // Se c'è un'unità di misura di default, carica le unità della stessa categoria
  const { data: sameCategoryUnits = [], isLoading: isLoadingSameCategory } = useQuery<string[]>(
    ['units-by-same-category', defaultUnitOfMeasurement],
    () =>
      apiClient.get_units_by_same_category_units_of_measure_by_same_category__udm__get(
        defaultUnitOfMeasurement!
      ),
    { enabled: !!defaultUnitOfMeasurement }
  );

  // Se non c'è un'unità di misura di default, carica tutte le unità raggruppate per categoria
  const { data: groupedUnits = {}, isLoading: isLoadingGrouped } = useQuery<{ [key: string]: string[] }>(
    'units-of-measure-grouped',
    () => apiClient.get_grouped_units_units_of_measure_grouped_get(),
    { enabled: !defaultUnitOfMeasurement }
  );

  // Determina le opzioni da mostrare
  const options = React.useMemo(() => {
    if (defaultUnitOfMeasurement && sameCategoryUnits.length > 0) {
      // Se c'è un'unità di default, mostra quella e le altre della stessa categoria
      return sameCategoryUnits;
    }
    // Altrimenti mostra tutte le unità disponibili (raggruppate per categoria)
    return groupedUnits;
  }, [defaultUnitOfMeasurement, sameCategoryUnits, groupedUnits]);

  const isLoading = defaultUnitOfMeasurement ? isLoadingSameCategory : isLoadingGrouped;
  const isGrouped = !defaultUnitOfMeasurement && Object.keys(groupedUnits).length > 0;

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    if (onChange) {
      onChange(newValue === '' ? null : newValue);
    }
  };

  // Se c'è un'unità di default e il valore è vuoto, usa quella di default
  const displayValue = value ?? defaultUnitOfMeasurement ?? '';

  return (
    <FormControl fullWidth={fullWidth} error={error} required={required} size={size}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={displayValue}
        onChange={handleChange}
        onBlur={onBlur}
        label={label}
        variant={variant}
        disabled={disabled || isLoading}
      >
        {isLoading ? (
          <MenuItem disabled>Caricamento...</MenuItem>
        ) : isGrouped ? (
          // Se raggruppato per categoria, mostra le categorie con ListSubheader
          Object.entries(options as { [key: string]: string[] }).map(([category, units]) => [
            <ListSubheader key={category}>{category}</ListSubheader>,
            ...units.map(unit => {
              const formattedLabel = formatUnitOfMeasurement(unit);
              return (
                <MenuItem key={unit} value={unit}>
                  {formattedLabel}
                </MenuItem>
              );
            }),
          ])
        ) : (
          // Altrimenti mostra la lista semplice
          (options as string[]).map(unit => {
            const isDefault = unit === defaultUnitOfMeasurement;
            const formattedLabel = formatUnitOfMeasurement(unit);
            const displayLabel =
              isDefault && showDefaultLabel
                ? `${formattedLabel} (default)`
                : formattedLabel;

            return (
              <MenuItem key={unit} value={unit}>
                {displayLabel}
              </MenuItem>
            );
          })
        )}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

